import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import maplibregl from 'maplibre-gl';
import { TbBuildingBank, TbMapPinFilled } from 'react-icons/tb';
import MapLibreMap, { MapRef, Marker } from 'react-map-gl/maplibre';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { geocodingQueryOptions, hasGeocodableLocation } from '@/queries';
import {
    createThemedMapStyle,
    ThemedMapControls,
    ThemedMapPopup,
} from '@/shared/components/data-visualization/maplibre';
import DropdownButtonSelect from '@/shared/components/dropdowns/DropdownButton/DropdownButtonSelect';
import { ReportComponentReadinessOverlay } from '@/shared/components/report-components';

import { FinancialOverviewMapPanelProps } from '../../types';
import { institutionalHoldingsQueryOptions } from '../FinancialOverviewInstitutionalHoldings/data';
import { resolveInstitutionLocation } from '../FinancialOverviewInstitutionalHoldings/institutionWebsites';
import { FinancialOverviewPanel } from '../FinancialOverviewPanel';
import { FinancialOverviewMapSkeleton } from '../FinancialOverviewSkeletons';
import {
    getFinancialOverviewLocationLabel,
    getFinancialOverviewMapZoom,
    ResolvedFinancialOverviewMapLocation,
    resolveFinancialOverviewMapLocation,
} from './location';

import 'maplibre-gl/dist/maplibre-gl.css';

const defaultMapView = { latitude: 38.5, longitude: -98.5, zoom: 2.75 };
const institutionalHoldersLocationId = 'institutional-holders';

interface FinancialOverviewMapMarker {
    key: string;
    label: string;
    location: ResolvedFinancialOverviewMapLocation;
    name: string;
}

export const FinancialOverviewMapPanel: FC<FinancialOverviewMapPanelProps> = ({
    location,
    locationDescription = 'Review headquarters and other reported company locations.',
    locations,
    tickerSymbol,
}) => {
    const { token, resolvedThemeName } = useAntDesignTheme();
    const mapRef = useRef<MapRef | null>(null);
    const [popupLocationKey, setPopupLocationKey] = useState<string>();
    const [selectedLocationId, setSelectedLocationId] = useState('headquarters');
    const locationOptions = useMemo(() => {
        const companyLocations = (locations?.length ? locations : [location ?? {}]).map((candidate, index) => ({
            ...candidate,
            id: candidate.id ?? (index === 0 ? 'headquarters' : `location-${index + 1}`),
            label: candidate.label ?? (index === 0 ? 'Headquarters' : `Location ${index + 1}`),
        }));

        return tickerSymbol
            ? [
                  ...companyLocations,
                  {
                      id: institutionalHoldersLocationId,
                      label: 'Top Institutional Holders',
                      name: 'Top Institutional Holders',
                  },
              ]
            : companyLocations;
    }, [location, locations, tickerSymbol]);
    const selectedLocation =
        locationOptions.find((candidate) => candidate.id === selectedLocationId) ?? locationOptions[0];
    const showingInstitutionalHolders = selectedLocation.id === institutionalHoldersLocationId;
    const institutionalHoldingsQuery = useQuery({
        ...institutionalHoldingsQueryOptions(tickerSymbol ?? ''),
        enabled: showingInstitutionalHolders && Boolean(tickerSymbol),
    });
    const providedLocation = showingInstitutionalHolders
        ? undefined
        : resolveFinancialOverviewMapLocation(selectedLocation);
    const canGeocode = !showingInstitutionalHolders && !providedLocation && hasGeocodableLocation(selectedLocation);
    const geocodingQuery = useQuery({
        ...geocodingQueryOptions.location(selectedLocation),
        enabled: canGeocode,
    });
    const markers = useMemo<FinancialOverviewMapMarker[]>(() => {
        if (showingInstitutionalHolders) {
            return (institutionalHoldingsQuery.data ?? []).slice(0, 10).flatMap((institution, index) => {
                const holderLocation = resolveInstitutionLocation(institution, index);
                const resolvedLocation = resolveFinancialOverviewMapLocation(holderLocation);

                return holderLocation && resolvedLocation
                    ? [
                          {
                              key: holderLocation.id ?? `institutional-holder-${index}`,
                              label: getFinancialOverviewLocationLabel(holderLocation),
                              location: resolvedLocation,
                              name: holderLocation.name ?? `Institution ${index + 1}`,
                          },
                      ]
                    : [];
            });
        }

        const resolvedLocation = resolveFinancialOverviewMapLocation(selectedLocation, geocodingQuery.data);

        return resolvedLocation
            ? [
                  {
                      key: selectedLocation.id ?? `${resolvedLocation.latitude}:${resolvedLocation.longitude}`,
                      label: getFinancialOverviewLocationLabel(selectedLocation),
                      location: resolvedLocation,
                      name: selectedLocation.name ?? selectedLocation.label ?? 'Company',
                  },
              ]
            : [];
    }, [geocodingQuery.data, institutionalHoldingsQuery.data, selectedLocation, showingInstitutionalHolders]);
    const mapCenter = useMemo(() => {
        if (!markers.length) {
            return undefined;
        }

        return {
            latitude: markers.reduce((sum, marker) => sum + marker.location.latitude, 0) / markers.length,
            longitude: markers.reduce((sum, marker) => sum + marker.location.longitude, 0) / markers.length,
        };
    }, [markers]);
    const zoom =
        showingInstitutionalHolders && markers.length > 1
            ? 2.25
            : markers[0]
              ? getFinancialOverviewMapZoom(markers[0].location)
              : 3;
    const popupMarker = markers.find((marker) => marker.key === popupLocationKey);
    const loading = showingInstitutionalHolders ? institutionalHoldingsQuery.isFetching : geocodingQuery.isFetching;
    const mapStyle =
        resolvedThemeName === 'dark'
            ? 'https://tiles.openfreemap.org/styles/dark'
            : 'https://tiles.openfreemap.org/styles/positron';
    const mapContainerStyle = createThemedMapStyle(token, {
        background: token.colorBgContainer,
        border: `1px solid ${token.colorBorder}`,
        borderRadius: token.borderRadius,
        height: 16 * 18,
        overflow: 'hidden',
        position: 'relative',
        width: '100%',
    });

    useEffect(() => {
        if (!mapCenter || !mapRef.current) {
            return;
        }

        if (showingInstitutionalHolders && markers.length > 1) {
            const bounds = markers.reduce(
                (currentBounds, marker) => currentBounds.extend([marker.location.longitude, marker.location.latitude]),
                new maplibregl.LngLatBounds(),
            );

            mapRef.current.getMap().fitBounds(bounds, { duration: 650, padding: 48 });
            return;
        }

        mapRef.current.getMap().flyTo({
            center: [mapCenter.longitude, mapCenter.latitude],
            duration: 650,
            zoom,
        });
    }, [mapCenter, markers, showingInstitutionalHolders, zoom]);

    const locationControl = (
        <div style={{ width: '14rem', maxWidth: '100%' }}>
            <DropdownButtonSelect<string>
                aria-label='Select location'
                matchTriggerWidth={false}
                menuMinWidth='15rem'
                onChange={(value) => {
                    if (!value) {
                        return;
                    }

                    setPopupLocationKey(undefined);
                    setSelectedLocationId(value);
                }}
                options={locationOptions.map((candidate) => ({
                    description:
                        candidate.id === institutionalHoldersLocationId
                            ? 'Map the largest reported institutional holders by their resolved headquarters.'
                            : getFinancialOverviewLocationLabel(candidate) ||
                              (candidate.id === 'headquarters'
                                  ? 'Primary reported company location.'
                                  : 'Reported company operating location.'),
                    icon: candidate.id === institutionalHoldersLocationId ? <TbBuildingBank /> : <TbMapPinFilled />,
                    label: candidate.label,
                    value: candidate.id,
                }))}
                placement='bottomRight'
                size='small'
                value={selectedLocation.id}
            />
        </div>
    );

    return (
        <FinancialOverviewPanel
            actions={locationControl}
            description={locationDescription}
            icon={<TbMapPinFilled style={{ verticalAlign: '-0.125rem' }} />}
            title='Location'
        >
            {markers.length > 0 && mapCenter ? (
                <div
                    className={`cc-themed-map${resolvedThemeName === 'dark' ? ' cc-themed-map--dark' : ''}`}
                    style={mapContainerStyle}
                >
                    <MapLibreMap
                        attributionControl={false}
                        initialViewState={{
                            longitude: mapCenter.longitude,
                            latitude: mapCenter.latitude,
                            zoom,
                        }}
                        mapStyle={mapStyle}
                        ref={mapRef}
                        style={{ height: '100%', width: '100%' }}
                        reuseMaps
                        mapLib={maplibregl}
                    >
                        <ThemedMapControls
                            mapRef={mapRef}
                            onReset={() => {
                                if (showingInstitutionalHolders && markers.length > 1) {
                                    const bounds = markers.reduce(
                                        (currentBounds, marker) =>
                                            currentBounds.extend([marker.location.longitude, marker.location.latitude]),
                                        new maplibregl.LngLatBounds(),
                                    );
                                    mapRef.current?.getMap().fitBounds(bounds, { duration: 650, padding: 48 });
                                    return;
                                }

                                mapRef.current?.getMap().flyTo({
                                    center: [mapCenter.longitude, mapCenter.latitude],
                                    duration: 650,
                                    zoom,
                                });
                            }}
                            showDirection={false}
                        />
                        {markers.map((marker) => (
                            <Marker
                                key={marker.key}
                                longitude={marker.location.longitude}
                                latitude={marker.location.latitude}
                                anchor='center'
                                onClick={(event) => {
                                    event.originalEvent.stopPropagation();
                                    setPopupLocationKey(marker.key);
                                }}
                            >
                                <TbMapPinFilled
                                    aria-label={`${marker.name} location`}
                                    size='1.125rem'
                                    style={{ color: token.blue6 }}
                                />
                            </Marker>
                        ))}
                        {popupMarker ? (
                            <ThemedMapPopup
                                icon={showingInstitutionalHolders ? <TbBuildingBank /> : <TbMapPinFilled />}
                                items={
                                    popupMarker.location.source === 'geocoded'
                                        ? [
                                              {
                                                  label: 'Precision',
                                                  value: `${popupMarker.location.precision} level (approximate)`,
                                              },
                                          ]
                                        : []
                                }
                                latitude={popupMarker.location.latitude}
                                longitude={popupMarker.location.longitude}
                                onClose={() => setPopupLocationKey(undefined)}
                                subtitle={popupMarker.label || 'Location details unavailable'}
                                title={popupMarker.name}
                            />
                        ) : null}
                    </MapLibreMap>
                </div>
            ) : loading ? (
                <div style={mapContainerStyle}>
                    <FinancialOverviewMapSkeleton />
                </div>
            ) : (
                <div
                    className={`cc-themed-map${resolvedThemeName === 'dark' ? ' cc-themed-map--dark' : ''}`}
                    style={mapContainerStyle}
                >
                    <MapLibreMap
                        attributionControl={false}
                        initialViewState={defaultMapView}
                        interactive={false}
                        mapLib={maplibregl}
                        mapStyle={mapStyle}
                        reuseMaps
                        style={{ height: '100%', width: '100%' }}
                    />
                    <ReportComponentReadinessOverlay
                        description={
                            showingInstitutionalHolders
                                ? 'No reliable headquarters locations are available for the reported top holders.'
                                : 'No usable address or coordinates are available for this profile.'
                        }
                        icon={
                            showingInstitutionalHolders ? (
                                <TbBuildingBank size='75%' style={{ color: token.colorTextTertiary }} />
                            ) : (
                                <TbMapPinFilled size='75%' style={{ color: token.colorTextTertiary }} />
                            )
                        }
                        title='No Location Found'
                    />
                </div>
            )}
        </FinancialOverviewPanel>
    );
};
